import {
  ACTIVITY_SHAPE_MORPH_MS,
  ACTIVITY_SHAPE_SEQUENCE,
} from '@/components/chat/agent-activity-sequence';

export type VoiceAvatarMode = 'listening' | 'speaking' | 'idle';
export type VoiceColor = readonly [
  lightness: number,
  chroma: number,
  hue: number,
];
export const VOICE_BASE_COLOR: VoiceColor = [0.69, 0.14, 281.4869466171821];
const SHADE_OFFSETS: readonly (readonly [number, number])[] = [
  [0, 0],
  [-16, -6],
  [12, 6],
  [-10, -3],
  [8, 4],
  [-14, -5],
  [16, 5],
  [-8, -1],
  [10, 3],
  [-12, -4],
  [14, 6],
  [-6, -2],
  [4, 2],
  [-16, -5],
  [16, 4],
  [-10, -6],
  [12, 5],
  [-14, -3],
  [8, 6],
  [-8, -4],
  [6, 3],
];
const HOLD_MS = ACTIVITY_SHAPE_SEQUENCE[0]!.holdMs;
const CYCLE_MS = ACTIVITY_SHAPE_MORPH_MS + HOLD_MS;
const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));
const elapsed = (time: number) =>
  Number.isFinite(time) ? Math.max(0, time) : 0;

/** Color interpolation uses the same ease-out-quart curve as SquishEngine. */
export function getListeningFrame(timeMs: number) {
  const time = elapsed(timeMs);
  if (time < HOLD_MS) return { index: 0, previous: 0, morphAt: 0, progress: 1 };
  const step = Math.floor((time - HOLD_MS) / CYCLE_MS) + 1;
  const index = step % ACTIVITY_SHAPE_SEQUENCE.length;
  const morphAt = HOLD_MS + (step - 1) * CYCLE_MS;
  const progress = clamp((time - morphAt) / ACTIVITY_SHAPE_MORPH_MS, 0, 1);
  return {
    index,
    previous:
      (index + ACTIVITY_SHAPE_SEQUENCE.length - 1) %
      ACTIVITY_SHAPE_SEQUENCE.length,
    morphAt,
    progress: 1 - (1 - progress) ** 4,
  };
}

function listeningShade(index: number): VoiceColor {
  const [chroma, lightness] = SHADE_OFFSETS[index]!;
  return [
    clamp(VOICE_BASE_COLOR[0] + lightness / 100, 0.2, 0.95),
    clamp(VOICE_BASE_COLOR[1] * (1 + chroma * 0.014), 0, 0.4),
    VOICE_BASE_COLOR[2],
  ];
}

export function mixVoiceColor(
  start: VoiceColor,
  end: VoiceColor,
  progress: number
): VoiceColor {
  const amount = Number.isFinite(progress) ? clamp(progress, 0, 1) : 0;
  return [
    start[0] + (end[0] - start[0]) * amount,
    start[1] + (end[1] - start[1]) * amount,
    start[2] + (end[2] - start[2]) * amount,
  ];
}

export function getVoiceColor(
  mode: VoiceAvatarMode,
  timeMs: number
): VoiceColor {
  if (mode === 'idle') return VOICE_BASE_COLOR;
  if (mode === 'listening') {
    const { index, previous, progress } = getListeningFrame(timeMs);
    return mixVoiceColor(
      listeningShade(previous),
      listeningShade(index),
      progress
    );
  }
  const phase = (elapsed(timeMs) % 850) / 850;
  const progress = (1 - Math.cos(phase * Math.PI * 2)) / 2;
  return mixVoiceColor(
    [
      VOICE_BASE_COLOR[0] - 0.2,
      VOICE_BASE_COLOR[1] * 1.21,
      VOICE_BASE_COLOR[2],
    ],
    [
      VOICE_BASE_COLOR[0] - 0.14,
      VOICE_BASE_COLOR[1] * 0.86,
      VOICE_BASE_COLOR[2],
    ],
    progress
  );
}

export function voiceGradientColor(
  color: VoiceColor,
  offset: -1 | 0 | 1
): VoiceColor {
  return [clamp(color[0] + offset * 0.012, 0.2, 0.95), color[1], color[2]];
}

export function voiceColorCss(color: VoiceColor) {
  return `oklch(${(color[0] * 100).toFixed(3)}% ${color[1].toFixed(4)} ${color[2].toFixed(2)})`;
}
